/**
 * Renova o token antes de ele vencer e reconfere quem é o dono dele.
 *
 * Sem a renovação o operador é deslogado no meio do turno — que é exatamente o
 * problema que a F7.2 existia para resolver. A renovação é silenciosa; o
 * usuário só volta à tela de login quando o teto de 24 h estoura.
 *
 * A reconferência (`/auth/me`) existe porque o papel não vem no token para a
 * tela: ele vinha só da resposta do login, e um F5 com sessão válida deixava o
 * painel sem papel nenhum — as abas de administrador sumiam sozinhas.
 */
import { useEffect } from "react";
import { useAuthStore } from "../store/auth.store.ts";

/** Renova com folga: rede ruim não pode transformar atraso em sessão perdida. */
const RENEW_MARGIN_MS = 5 * 60 * 1000;

export function useSessionKeeper(): void {
  const token = useAuthStore((state) => state.token);
  const expiresAt = useAuthStore((state) => state.expiresAt);
  const refresh = useAuthStore((state) => state.refresh);
  const hydrate = useAuthStore((state) => state.hydrate);

  // Uma vez por subida do painel, com token na mão. Quem manda sobre o papel é
  // o servidor: a cópia guardada pode ser de antes de a pessoa ser promovida —
  // ou rebaixada.
  useEffect(() => {
    if (!token) return;
    void hydrate();
  }, [token, hydrate]);

  useEffect(() => {
    if (!token || !expiresAt) return;
    const delay = Math.max(0, expiresAt - Date.now() - RENEW_MARGIN_MS);
    const timer = setTimeout(() => void refresh(), delay);
    return () => clearTimeout(timer);
  }, [token, expiresAt, refresh]);

  // Aba em segundo plano tem os temporizadores estrangulados pelo navegador: ao
  // voltar, o prazo pode já ter passado. Sem esta verificação, quem deixa o
  // painel aberto numa aba secundária volta para a tela de login sem motivo.
  useEffect(() => {
    function onVisible(): void {
      if (document.visibilityState !== "visible") return;
      const { expiresAt: current, refresh: renew } = useAuthStore.getState();
      if (current && current - Date.now() < RENEW_MARGIN_MS) void renew();
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
}
